/*
 * Copyright (c) Castle Point Learning Systems Corporation, 2013-2023, all rights reserved.
 * NOTICE: Dissemination of the information and/or materials contained in this file is strictly
 * forbidden unless prior written permission is obtained from Castle Point Learning Systems Corporation.
 * See LICENSE.txt (part of this package) for the full notice.
 */

import { useCallback, useContext } from 'react';
import { EvaluationContext } from '../context/EvaluationContextProvider';
import { alignLines, calcEqualityOffset, calculateParts, findCurrentPartIndex } from '../utils/evaluationUtils';

// synchronization between editors and model
export const useEvaluationUpdate = (lineId: string) => {
    const { dispatchEvaluation } = useContext(EvaluationContext);

    // We have to prevent cursor update on initial render due to cursor position set manually (to prevent extra renders)
    return useCallback(
        (initialUpdate: boolean = false) => {
            dispatchEvaluation((evaluation) => {
                const lines = evaluation.lines.map((line) =>
                    lineId === line.id
                        ? calcEqualityOffset({
                              ...line,
                              tree: line.editor.getTree(),
                          })
                        : line
                );
                const line = lines.find((l) => l.id === lineId);
                const activePartIndex = findCurrentPartIndex(line);
                const parts = calculateParts(line.tree);

                let cursor = evaluation.activeLine;

                if (!initialUpdate && (evaluation.activeLine.activePartIndex !== activePartIndex || evaluation.activeLine.partsCount !== parts.length)) {
                    cursor = { lineId, activePartIndex, partsCount: parts.length };
                }
                return {
                    ...evaluation,
                    dirty: true,
                    focused: true,
                    lines: alignLines(lines),
                    activeLine: cursor,
                };
            });
        },
        [lineId, dispatchEvaluation]
    );
};
